import { useState, useEffect, useRef } from 'react';
import {
  fetchScheduledResourcesByDateWindow,
  fetchTicketsByIds,
  fetchPaycorTimeOff,
  fetchSoftSchedules,
  getResourceName,
} from '../api/client';
import { computePersonUtilization } from '../utils/utilizationCalc';

function toIsoZ(d) {
  const iso = new Date(d).toISOString();
  return iso.slice(0, 19) + 'Z';
}

// Count Mon–Fri days in the overlap of two date ranges.
function weekdaysInOverlap(aStart, aEnd, bStart, bEnd) {
  const overlapStart = new Date(Math.max(aStart.getTime(), bStart.getTime()));
  const overlapEnd = new Date(Math.min(aEnd.getTime(), bEnd.getTime()));
  if (overlapEnd < overlapStart) return 0;
  let count = 0;
  const d = new Date(overlapStart);
  d.setHours(0, 0, 0, 0);
  while (d <= overlapEnd) {
    const dow = d.getDay();
    if (dow >= 1 && dow <= 5) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

/**
 * Load and join utilization data for a single Sunday–Saturday week.
 *
 * @param {object} params
 * @param {string|null}  params.token             - API auth token
 * @param {string|null}  params.weekStartIso      - ISO date string for the week's Sunday (YYYY-MM-DD)
 * @param {string[]}     params.memberIdentifiers - CW member identifiers to filter to ([] = all)
 * @param {string}       params.projectFilter     - comma-separated project IDs to filter to ('' = all)
 * @param {object[]}     params.users             - member records from fetchUsersByWorkRoleIds (for work-type lookup)
 * @param {boolean}      params.enabled           - gate: only fetch when true
 *
 * @returns {{ loading, error, ticketRows, personRows, allTickets }}
 */
export default function useUtilizationData({
  token,
  weekStartIso,
  memberIdentifiers = [],
  projectFilter = '',
  users = [],
  enabled = true,
  version = 0,
  softVersion = 0,
}) {
  const [state, setState] = useState({
    loading: false,
    error: null,
    ticketRows: [],
    personRows: [],
    allTickets: [],
  });
  const [softScheduleRows, setSoftScheduleRows] = useState([]);

  // Shared so the independent soft-schedule effect can enrich member names without re-fetching users
  const userMapRef = useRef(new Map());

  // Keep mutable refs so the async closure always reads latest values
  const usersRef = useRef(users);
  useEffect(() => { usersRef.current = users; }, [users]);

  const memberDepKey = memberIdentifiers.slice().sort().join(',');
  const usersDepKey = users.length; // re-run if roster size changes

  useEffect(() => {
    if (!enabled || !weekStartIso || !token) return;

    let cancelled = false;
    setState(prev => ({ ...prev, loading: true, error: null }));

    (async () => {
      try {
        // Week boundaries: Sunday 00:00 → Saturday 23:59:59
        const weekStart = new Date(weekStartIso + 'T00:00:00');
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const startIso = toIsoZ(weekStart);
        const endIso = toIsoZ(weekEnd);

        // 1 – Fetch all scheduled resources in the week in a single request
        const srOptions = { pageSize: 200 };
        if (memberIdentifiers.length > 0) {
          srOptions.memberIdentifiers = memberIdentifiers;
        }
        const resources = await fetchScheduledResourcesByDateWindow(startIso, endIso, token, srOptions);
        if (cancelled) return;

        // 2 – Fetch tickets referenced by either set of resources
        const ticketIdSet = new Set(
          resources
            .map(r => r?.objectId)
            .filter(id => id != null)
            .map(String)
        );
        let allTickets = [];
        if (ticketIdSet.size > 0) {
          const rawTickets = await fetchTicketsByIds(Array.from(ticketIdSet), token);
          if (cancelled) return;
          allTickets = Array.isArray(rawTickets) ? rawTickets : [];
        }

        // 3 – Apply project filter (narrows the visible ticket set)
        const trimmedFilter = (projectFilter || '').trim();
        let validTicketIds;
        if (trimmedFilter) {
          const parts = trimmedFilter.split(',').map(s => s.trim()).filter(Boolean);
          const kept = allTickets.filter(t => {
            const pid = String(t?.project?.id ?? t?.projectId ?? '');
            return parts.some(f => pid.includes(f));
          });
          validTicketIds = new Set(kept.map(t => String(t?.id ?? t?.ticketId ?? '')));
        } else {
          validTicketIds = new Set(allTickets.map(t => String(t?.id ?? t?.ticketId ?? '')));
        }

        // 4 – PTO for the week
        const startDateStr = weekStart.toISOString().slice(0, 10);
        const endDateStr = weekEnd.toISOString().slice(0, 10);
        let ptoRecords = [];
        try {
          ptoRecords = (await fetchPaycorTimeOff(startDateStr, endDateStr, token)) || [];
        } catch (e) {
          console.warn('[useUtilizationData] PTO fetch failed', e);
        }
        if (cancelled) return;

        // Build lookup maps
        const ticketMap = new Map();
        allTickets.forEach(t => {
          const tid = String(t?.id ?? t?.ticketId ?? '');
          if (tid) ticketMap.set(tid, t);
        });

        const currentUsers = usersRef.current;
        const userMap = new Map();
        currentUsers.forEach(u => {
          const id = u?.identifier || String(u?.id ?? '');
          if (id) userMap.set(id, u);
        });
        userMapRef.current = userMap; // expose for soft schedule effect

        // PTO hours per employee — keyed by cw_users id (matches Paycor employeeNumber)
        const ptoByEmpNum = new Map();
        // PTO days list per employee: [{ date: Date, dayName: string, hours: number }]
        const ptoDaysByEmpNum = new Map();
        const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

        (ptoRecords || []).forEach(r => {
          if (!r?.start_time) return;
          const ptoStart = new Date(r.start_time);
          const ptoEnd = r.end_time ? new Date(r.end_time) : new Date(r.start_time);

          // Enumerate every weekday in the overlap between PTO range and the current week
          const overlapStart = new Date(Math.max(ptoStart.getTime(), weekStart.getTime()));
          const overlapEnd = new Date(Math.min(ptoEnd.getTime(), weekEnd.getTime()));
          const days = [];
          if (overlapEnd >= overlapStart) {
            const d = new Date(overlapStart);
            d.setHours(0, 0, 0, 0);
            const endDay = new Date(overlapEnd);
            endDay.setHours(0, 0, 0, 0);
            while (d <= endDay) {
              const dow = d.getDay();
              if (dow >= 1 && dow <= 5) {
                // Hours for this day: if record has total hours and spans multiple days, distribute evenly;
                // otherwise use 8h per weekday as default.
                days.push({ date: new Date(d), dayName: DAY_NAMES[dow] });
              }
              d.setDate(d.getDate() + 1);
            }
          }

          // Determine hours: if the record specifies hours, divide evenly across the overlapping days
          let totalHrs;
          if (r.hours != null) {
            totalHrs = Number(r.hours);
          } else {
            totalHrs = days.length * 8;
          }
          if (!(totalHrs > 0) || days.length === 0) return;
          const hrsPerDay = totalHrs / days.length;

          if (r.employeeNumber) {
            const k = String(r.employeeNumber);
            ptoByEmpNum.set(k, (ptoByEmpNum.get(k) || 0) + totalHrs);
            const existing = ptoDaysByEmpNum.get(k) || [];
            days.forEach(day => existing.push({ ...day, hours: hrsPerDay }));
            ptoDaysByEmpNum.set(k, existing);
          }
        });


        // Resources that pass the ticket/project filter
        const validResources = resources.filter(r =>
          r?.objectId != null && validTicketIds.has(String(r.objectId))
        );

        // ── View A: Ticket Rows ──────────────────────────────────────────────
        const ticketRowMap = new Map();
        validResources.forEach(r => {
          const tid = String(r.objectId);
          const ticket = ticketMap.get(tid);
          if (!ticket) return;

          const hrs = Number(r?.hours ?? r?.scheduledHours ?? 0);
          const sched = r?.doneFlag ? 0 : hrs;
          const actual = r?.doneFlag ? hrs : 0;
          const memberName = getResourceName(r);
          const memberIdentifier = String(
            r?.member?.identifier ?? r?.memberIdentifier ?? r?.member?.id ?? ''
          );

          if (!ticketRowMap.has(tid)) {
            ticketRowMap.set(tid, {
              ticketId: tid,
              ticketNumber: ticket?.id ?? ticket?.ticketNumber ?? tid,
              summary: ticket?.summary || ticket?.subject || '(no summary)',
              projectName: ticket?.project?.name || ticket?.projectName || '',
              projectId: String(ticket?.project?.id ?? ticket?.projectId ?? ''),
              taskType:
                ticket?.taskType ||
                ticket?.type?.name ||
                ticket?.subType?.name ||
                '',
              taskInfo: ticket?.taskInfo || '',
              budgetHours: ticket?.budgetHours ?? ticket?.budget ?? null,
              dateStart: ticket?.actualStartDate || ticket?.scheduleStartDate || ticket?.expectedStartDate || ticket?.estimatedStartDate || null,
              dateEnd: ticket?.actualEndDate || ticket?.scheduleEndDate || ticket?.estimatedEndDate || ticket?.expectedEndDate || null,
              status: ticket?.status?.name || ticket?.statusName || null,
              resources: [],
            });
          }

          const row = ticketRowMap.get(tid);
          const existing = row.resources.find(
            x => x.memberIdentifier && x.memberIdentifier === memberIdentifier
          );
          if (existing) {
            existing.scheduledHours += sched;
            existing.actualHours += actual;
          } else {
            row.resources.push({ memberName, memberIdentifier, scheduledHours: sched, actualHours: actual });
          }
        });

        const ticketRows = Array.from(ticketRowMap.values()).sort((a, b) =>
          (a.summary || '').localeCompare(b.summary || '')
        );

        // ── View B: Person Rows ──────────────────────────────────────────────
        const personMap = new Map();

        // Pre-seed so selected users appear even when they have no assignments this week
        if (memberIdentifiers.length > 0) {
          for (const ident of memberIdentifiers) {
            if (!ident) continue;
            const user = userMap.get(ident);
            const memberName = user?.name || user?.fullName || ident;
            const workType =
              user?.workRole?.name ||
              (typeof user?.workRole === 'string' ? user.workRole : '') ||
              '';
            personMap.set(ident, { memberName, memberIdentifier: ident, workType, scheduledHours: 0, actualHours: 0 });
          }
        }

        validResources.forEach(r => {
          const memberIdentifier = String(
            r?.member?.identifier ?? r?.memberIdentifier ?? r?.member?.id ?? ''
          );
          const memberName = getResourceName(r);
          const hrs2 = Number(r?.hours ?? r?.scheduledHours ?? 0);
          const sched2 = r?.doneFlag ? 0 : hrs2;
          const actual2 = r?.doneFlag ? hrs2 : 0;
          const key = memberIdentifier || memberName;
          if (!key) return;

          if (!personMap.has(key)) {
            const user = userMap.get(memberIdentifier) || null;
            const workType =
              user?.workRole?.name ||
              (typeof user?.workRole === 'string' ? user.workRole : '') ||
              '';
            personMap.set(key, { memberName, memberIdentifier, workType, scheduledHours: 0, actualHours: 0 });
          }
          personMap.get(key).scheduledHours += sched2;
          personMap.get(key).actualHours += actual2;
        });

        const personRows = Array.from(personMap.values()).map(p => {
          let ptoHours = 0;
          let ptoDays = [];
          const user = userMap.get(p.memberIdentifier);
          const empNum = user?.id;
          if (empNum) {
            ptoHours = ptoByEmpNum.get(String(empNum)) || 0;
            ptoDays = (ptoDaysByEmpNum.get(String(empNum)) || []).sort((a, b) => a.date - b.date);
          }
          return { ...p, ptoDays, ...computePersonUtilization({ scheduledHours: p.scheduledHours, actualHours: p.actualHours, ptoHours }) };
        });

        // Sort: blank work type last, then by work type alpha, then by name
        personRows.sort((a, b) => {
          const wa = a.workType || '\uFFFF';
          const wb = b.workType || '\uFFFF';
          if (wa !== wb) return wa.localeCompare(wb);
          return (a.memberName || '').localeCompare(b.memberName || '');
        });

        if (!cancelled) {
          setState({ loading: false, error: null, ticketRows, personRows, allTickets });
        }
      } catch (e) {
        if (!cancelled) setState(prev => ({ ...prev, loading: false, error: e }));
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, weekStartIso, token, memberDepKey, projectFilter, usersDepKey, version]);

  // Independent effect: only re-runs when softVersion changes, avoids full data reload
  useEffect(() => {
    if (!enabled || !weekStartIso || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const weekStart = new Date(weekStartIso + 'T00:00:00');
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        const startIso = toIsoZ(weekStart);
        const endIso = toIsoZ(weekEnd);
        const all = (await fetchSoftSchedules(startIso, token, endIso)) || [];
        if (cancelled) return;
        const rows = all
          .filter(ss => {
            if (!ss?.dateStart) return false;
            const ssStart = new Date(ss.dateStart);
            const ssEnd = ss.dateEnd ? new Date(ss.dateEnd) : ssStart;
            return ssEnd >= weekStart && ssStart <= weekEnd;
          })
          .map(ss => {
            const user = userMapRef.current.get(ss.memberIdentifier) || null;
            const memberName = user?.name || user?.fullName || ss.memberIdentifier || '';
            return { ...ss, memberName };
          });
        if (!cancelled) setSoftScheduleRows(rows);
      } catch (e) {
        console.warn('[useUtilizationData] soft schedule fetch failed', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, weekStartIso, token, softVersion]);

  // Prorate each soft schedule's hours to the fraction that falls within this week,
  // accumulate per member, and collect individual entries for display.
  const softHoursByMember = new Map();   // memberIdentifier → prorated hours total
  const softEntriesByMember = new Map(); // memberIdentifier → [{ label, hrsForWeek }]
  if (weekStartIso) {
    const ws = new Date(weekStartIso + 'T00:00:00');
    const we = new Date(ws);
    we.setDate(we.getDate() + 6);
    we.setHours(23, 59, 59, 999);
    softScheduleRows.forEach(ss => {
      if (!ss?.memberIdentifier) return;
      const hrs = Number(ss.hours);
      if (!(hrs > 0)) return;
      const ssStart = new Date(ss.dateStart);
      const ssEnd = ss.dateEnd ? new Date(ss.dateEnd) : ssStart;
      const totalWeekdays = weekdaysInOverlap(ssStart, ssEnd, ssStart, ssEnd);
      if (totalWeekdays === 0) return;
      const overlapWeekdays = weekdaysInOverlap(ssStart, ssEnd, ws, we);
      const hrsForWeek = hrs * (overlapWeekdays / totalWeekdays);
      if (!(hrsForWeek > 0)) return;
      const key = ss.memberIdentifier;
      softHoursByMember.set(key, (softHoursByMember.get(key) || 0) + hrsForWeek);
      const label = ss.ticketInfo || ss.projectInfo || ss.description || '(no description)';
      const entries = softEntriesByMember.get(key) || [];
      entries.push({ label, hrsForWeek });
      softEntriesByMember.set(key, entries);
    });
  }

  const enrichedPersonRows = state.personRows.map(p => {
    const softScheduledHours = softHoursByMember.get(p.memberIdentifier) || 0;
    const softSchedules = softEntriesByMember.get(p.memberIdentifier) || [];
    const metrics = computePersonUtilization({
      scheduledHours: p.scheduledHours,
      softScheduledHours,
      actualHours: p.actualHours,
      ptoHours: p.ptoHours,
    });
    return { ...p, ...metrics, softSchedules };
  });

  return { ...state, personRows: enrichedPersonRows, softScheduleRows };
}
